import { Box, Stack, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useCurrentUser } from "lib/store/use-current-user"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { FitInRemainingSpace } from "lib/@components/fit-in-remaining-space"
import { Repeat } from "lib/@components/repeat"
import { parentFind } from "library/tree/index.mjs"
import { PlanRoot } from "library/tree/constants.mjs"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import {
    getShareLinkCommentSummary,
    getShareLinkRegimeUnread,
    markShareLinkRegimeCommentThreadsRead,
    resolveShareLinkScheduleComment,
} from "routes/schedule/plugins/share-link-comments/share-link-comments.controller"
import { ResponsesRegimePanel } from "routes/sharing/share-link-fields/responses-regime-panel"
import { RegimeNavigator } from "routes/asset-register/plugins/regime-display/regime-navigator"
import { filterCommentsToFolderScope } from "./share-link-comment-folder-scope"
import { getShareLinkCommentRegimeId } from "./share-link-comment-regime-id"
import { useShareLinkCommentReadTracker } from "./use-share-link-comment-read-tracker"
import { ShareLinkCommentRegimeToolbar } from "./share-link-comment-regime-toolbar"
import { ShareLinkCommentScheduleCard } from "./share-link-comment-schedule-card"
import { ShareLinkCommentsExportButton } from "./share-link-comments-export-button"
import { ShareLinkRegimeResetButton } from "./share-link-regime-reset-button"

const EMPTY_GROUPS = []
const EMPTY_UNREAD = { total: 0, unreadThreadIds: [] }

// Regime-level Comments directory: an internal reviewer's triage view across every share link in the
// regime. It is a routing surface — each row's primary action jumps to the comment's native context
// where replies happen. Here we only triage: filter by status / recipient / schedule, and
// resolve/reopen inline. Every recipient's comments merge into one date-sorted stream — the host sees
// everyone together (each row names its author). Per-recipient isolation is enforced separately on the
// recipient read path, so flattening here is host-only presentation and leaks nothing between sharees.
export function ShareLinkCommentsRegimePanel({ hideWhenEmpty = false }) {
    const { parentItem, facility, facilityGroup } = useBoundContext()
    const currentUser = useCurrentUser()
    const itemId = parentItem?.id ?? facility?.id ?? facilityGroup?.id
    const rootItem = parentFind(parentItem ?? facility ?? facilityGroup, isPlanRoot)
    const regimeId = getShareLinkCommentRegimeId({ itemId, rootItem })
    const summary = getShareLinkCommentSummary.useResults(
        { regimeId, facilityId: regimeId ? itemId : undefined },
        withInitialValue(IS_LOADING)
    )
    const [status] = useSearchParam("regimeCommentFilter", "open")
    const [recipient] = useSearchParam("regimeCommentRecipient", "all")
    const [target] = useSearchParam("regimeCommentTarget", "all")
    const [incomingTarget] = useSearchParam("commentTarget", null)
    // Each summary row marks its thread read once it has dwelled in view (see
    // useMarkCommentThreadReadOnView on the row). The tracker batches those signals into one mark-read
    // call and clears the tab badge; new recipient activity re-populates the unread query.
    const unread = getShareLinkRegimeUnread.useResults({ regimeId }) ?? EMPTY_UNREAD
    const { unreadThreadSet, markThreadSeen } = useShareLinkCommentReadTracker({
        unreadThreadIds: unread.unreadThreadIds,
        mark: markReadThreads,
    })

    if (!regimeId) return null
    if (summary === IS_LOADING) return <LoadingScreen />

    const groups = summary ?? EMPTY_GROUPS
    const allComments = flattenComments(groups)
    const scopeItem = parentItem ?? facility ?? facilityGroup
    const scopedComments = filterCommentsToFolderScope(allComments, scopeItem)
    const visibleRows = getVisibleRows(allComments, {
        status,
        recipient,
        target: resolveTarget(),
        scopeItem,
    })
    // The card's "N Comments" reflects root threads, not replies or unread state, and stays stable
    // regardless of the open/resolved/recipient filter narrowing the rows.
    const scheduleThreadCounts = getScheduleThreadCounts(scopedComments)
    const scheduleGroups = getScheduleGroupItems(visibleRows, scheduleThreadCounts)

    if (hideWhenEmpty && !scopedComments.length) return null

    return (
        <Box data-cy="share-link-regime-comments-panel">
            <RegimeNavigator sx={{ width: "100%", flexShrink: 0 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <ShareLinkCommentsExportButton
                        regimeId={regimeId}
                        folderId={getExportFolderId()}
                        folderName={getExportFolderName()}
                    />
                    <ShareLinkRegimeResetButton
                        regimeId={regimeId}
                        folderId={getExportFolderId()}
                        folderName={getExportFolderName()}
                    />
                </Stack>
            </RegimeNavigator>
            <Stack
                data-cy="share-link-comments-with-responses-layout"
                direction={{ xs: "column", lg: "row" }}
                spacing={2}
                alignItems="flex-start"
            >
                <ResponsesRegimePanel
                    title="Requested Information"
                    dataCy="share-link-comments-required-field-responses"
                    hideWhenEmpty
                    maxHeight={{ xs: "45vh", lg: "calc(100vh - 360px)" }}
                    contentSx={{ pt: { lg: 4 } }}
                    sx={{
                        width: { xs: "100%", lg: 360 },
                        flexShrink: 0,
                    }}
                />
                <Stack
                    data-cy="share-link-comments-list-column"
                    spacing={1.5}
                    sx={{ flex: 1, minWidth: 0, width: "100%" }}
                >
                    <ShareLinkCommentRegimeToolbar comments={scopedComments} />
                    <Bound
                        onResolveComment={onResolveComment}
                        commentViewer={getCommentViewer()}
                        markThreadSeen={markThreadSeen}
                        unreadThreadSet={unreadThreadSet}
                    >
                        {getCommentsList()}
                    </Bound>
                </Stack>
            </Stack>
        </Box>
    )

    // An explicit schedule pick wins; otherwise adopt an incoming commentTarget schedule id (set when
    // a schedule-code indicator opened this tab). This mirrors the toolbar's resolution so the panel
    // and the toolbar's Select always agree on the active schedule.
    function resolveTarget() {
        if (target && target !== "all") return target
        if (incomingTarget && scopedComments.some((comment) => comment.scheduleId === incomingTarget))
            return incomingTarget
        return "all"
    }

    // Same shape as the schedule tab's viewer so rows can caption the current user's comments "You".
    function getCommentViewer() {
        return { userId: currentUser?.id }
    }

    function markReadThreads({ threadIds }) {
        if (!regimeId) return undefined
        return markShareLinkRegimeCommentThreadsRead({ regimeId, threadIds })
    }

    async function onResolveComment(comment, resolved) {
        try {
            await resolveShareLinkScheduleComment({
                shareLinkId: comment.shareLinkId,
                regimeId: comment.regimeId ?? regimeId,
                scheduleId: comment.scheduleId,
                threadId: comment.threadId ?? comment.id,
                resolved,
            })
        } catch (e) {
            errorSnackbar("Couldn't update the comment")
        }
    }

    function getExportFolderId() {
        if (!scopeItem || scopeItem[PlanRoot]) return undefined
        return scopeItem.id ?? scopeItem._id
    }

    function getExportFolderName() {
        return scopeItem?.label ?? scopeItem?.name
    }

    function getCommentsList() {
        if (!scheduleGroups.length) {
            return (
                <Typography variant="body2" color="text.secondary">
                    No comments
                </Typography>
            )
        }

        return (
            <FitInRemainingSpace
                data-cy="share-link-regime-comments-virtual-list"
                setHeight
                leaveSpace={24}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflowX: "hidden",
                    pr: 1,
                }}
            >
                <Stack spacing={1.5}>
                    <Repeat
                        list={scheduleGroups}
                        pass="group"
                        keyFn={getScheduleGroupKey}
                        item={<ShareLinkCommentScheduleCard />}
                    />
                </Stack>
            </FitInRemainingSpace>
        )
    }
}

// Exported for direct unit testing. Apply the toolbar's status / recipient / schedule filters to the
// flat list of regime summary comments.
export function getVisibleRows(comments, { status = "open", recipient = "all", target = "all", scopeItem } = {}) {
    return filterCommentsToFolderScope(comments, scopeItem).filter(matches)

    function matches(comment) {
        return matchesStatus(comment) && matchesRecipient(comment) && matchesTarget(comment)
    }

    function matchesStatus(comment) {
        if (status === "open") return !comment.resolved
        if (status === "resolved") return !!comment.resolved
        return true
    }

    function matchesRecipient(comment) {
        if (recipient === "all") return true
        return (comment.recipientName ?? comment.authorName) === recipient
    }

    function matchesTarget(comment) {
        if (target === "all") return true
        return comment.scheduleId === target
    }
}

// Roll the flat comment stream up into one card per schedule (newest activity first). Comments from
// every share link merge together — the host triages all recipients per schedule, and each card jumps
// to that schedule's Comments tab. Each group carries the newest comment as its routing representative,
// a root-thread comment count, and the distinct commenters for the avatar stack.
export function getScheduleGroupItems(comments, messageCountByKey) {
    const groups = new Map()
    for (const comment of comments ?? []) {
        const key = getScheduleKey(comment)
        const existing = groups.get(key)
        if (existing) {
            existing.comments.push(comment)
        } else {
            groups.set(key, { key, comments: [comment] })
        }
    }
    return [...groups.values()].map((group) => buildScheduleGroup(group, messageCountByKey)).sort(byNewestFirst)
}

function getScheduleKey(comment) {
    return comment.schedulePath ?? comment.scheduleId ?? comment.id
}

function buildScheduleGroup({ key, comments }, messageCountByKey) {
    const sorted = [...comments].sort(byNewestFirst)
    const latest = sorted[0] ?? {}
    return {
        id: key,
        scheduleId: latest.scheduleId,
        scheduleTitle: latest.scheduleTitle,
        scheduleCode: latest.scheduleCode,
        scheduleLocation: latest.scheduleLocation,
        scheduleVersion: latest.scheduleVersion,
        regimeVersion: latest.regimeVersion,
        shareLinkId: latest.shareLinkId,
        draftSchedulePath: latest.draftSchedulePath,
        updated: latest.updated,
        // Prefer the schedule's full (unfiltered) thread counts when supplied; fall back to these rows.
        commentCount: messageCountByKey?.get(key)?.total ?? countComments(comments),
        resolvedCount: messageCountByKey?.get(key)?.resolved ?? countResolved(comments),
        commenters: getUniqueCommenters(sorted),
        threadIds: getThreadIds(comments),
        comment: latest,
    }
}

// Per-schedule thread counts, keyed the same way groups are: `total` across all statuses and the
// `resolved` subset. Built from the unfiltered comment set so the card's counts are the schedule's
// whole conversation, not just the rows surviving the active filter.
export function getScheduleThreadCounts(comments) {
    const counts = new Map()
    for (const comment of comments ?? []) {
        const key = getScheduleKey(comment)
        const existing = counts.get(key) ?? { total: 0, resolved: 0 }
        existing.total += 1
        if (comment.resolved) existing.resolved += 1
        counts.set(key, existing)
    }
    return counts
}

// Every thread id in the group, so the card can flag itself unread when any of its threads carry
// unseen content and mark them all read when the host opens the schedule.
function getThreadIds(comments) {
    return (comments ?? []).map((comment) => comment.threadId ?? comment.id)
}

// Root threads only, so the card's "N Comments" matches the schedule summary and not reply count.
function countComments(comments) {
    return comments?.length ?? 0
}

// Root threads that have been resolved.
function countResolved(comments) {
    return (comments ?? []).reduce((total, comment) => total + (comment.resolved ? 1 : 0), 0)
}

// Distinct thread authors, newest activity first, keyed by user (falling back to name). Replies don't
// appear in the summary so the stack reflects the people who started the threads on this schedule.
function getUniqueCommenters(sorted) {
    const seen = new Set()
    const commenters = []
    for (const comment of sorted) {
        const displayName = comment.recipientName ?? comment.authorName ?? "Commenter"
        const userKey = comment.authorUserId ?? displayName
        if (seen.has(userKey)) continue
        seen.add(userKey)
        commenters.push({ userId: comment.authorUserId, displayName })
    }
    return commenters
}

function byNewestFirst(a, b) {
    return getUpdatedTime(b) - getUpdatedTime(a)
}

function getUpdatedTime(comment) {
    const time = new Date(comment?.updated).getTime()
    return Number.isNaN(time) ? 0 : time
}

function flattenComments(groups) {
    const comments = []
    for (const group of groups ?? []) {
        for (const comment of group?.comments ?? []) {
            comments.push({ ...comment, regimeVersion: comment.regimeVersion ?? group.regimeVersion })
        }
    }
    return comments
}

function isPlanRoot(item) {
    return !!item?.[PlanRoot]
}

function getScheduleGroupKey(group) {
    return group?.id
}
